import { useState, useEffect, useRef, useCallback, FC } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { useAuth } from 'store/auth/useAuth'

import { postAxiosSingle } from 'pages/api/axios'
import { A_p_playPodcast } from 'utils/analytics/library/podcast'
import { createQueryUnAuth } from 'components/webinar/webinar-active/player/video/utils'
import { BASE_URL } from 'constants/url'
import { getPercent } from '../utils'
import {
  IconBackward,
  IconForward,
  IconPause,
  IconPlay,
  IconVolumeMax,
  IconVolumeMin,
  IconVolumeMuted,
} from './icons'

import style from '../audio-player.module.scss'
import progressStyle from './progress-bar.module.scss'

const INIT_VOLUME = 80
const REWIND_AMOUNT = 15

interface IControls {
  audioRef: React.RefObject<HTMLAudioElement>
  progressBarRef: React.RefObject<HTMLInputElement>
  duration: number
  setTimeProgress: (t: number) => void
  materialId?: number
}

const Controls: FC<IControls> = ({
  audioRef,
  progressBarRef,
  duration,
  setTimeProgress,
  materialId,
}) => {
  const { isNotAuthorized, isAuthorized, fakeAuth } = useAuth()
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(INIT_VOLUME)
  const playAnimationRef = useRef<number>()
  const counterStart = useRef(0)
  const router = useRouter()

  const isMuted = volume < 5
  const isLowVolume = volume < 40

  const repeat = useCallback(() => {
    if (!audioRef.current || !progressBarRef.current || !isPlaying) return

    const currentTime = audioRef.current.currentTime
    setTimeProgress(currentTime)
    progressBarRef.current.value = currentTime.toString()
    progressBarRef.current.style.setProperty(
      '--range-progress',
      getPercent(progressBarRef.current.value, duration)
    )

    playAnimationRef.current = requestAnimationFrame(repeat)
  }, [audioRef.current, progressBarRef.current, duration, isPlaying, setTimeProgress])

  const skipForward = () => {
    if (audioRef.current) audioRef.current.currentTime += REWIND_AMOUNT
  }

  const skipBackward = () => {
    if (audioRef.current) audioRef.current.currentTime -= REWIND_AMOUNT
  }

  const togglePlayPause = () => {
    if (!isPlaying && materialId && counterStart.current === 0) {
      if (isAuthorized && !fakeAuth) {
        postAxiosSingle(
          `${BASE_URL.PODCAST}/startListening`,
          {},
          { materialId, startListening: true }
        )
      }

      if (fakeAuth && router.asPath.includes('maid=' || 'maId=')) {
        postAxiosSingle(
          `${BASE_URL.PODCAST}/startListening/${fakeAuth}`,
          {},
          { materialId, startListening: true }
        )
      }

      if (fakeAuth && router.query.spec && router.asPath.includes('spec=')) {
        postAxiosSingle(
          createQueryUnAuth(router.query.spec),
          {},
          { materialId, materialType: 'podcast' }
        )
      }
    }
    setIsPlaying((prev) => !prev)
    if (!isPlaying) A_p_playPodcast()
    counterStart.current += 1
  }
  const toggleMute = () => setVolume(isMuted ? INIT_VOLUME : 0)

  useEffect(() => {
    if (isPlaying) audioRef?.current?.play()
    else audioRef?.current?.pause()

    playAnimationRef.current = requestAnimationFrame(repeat)
  }, [isPlaying, audioRef, repeat])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100
      audioRef.current.muted = isMuted
    }
  }, [volume, audioRef])

  return (
    <div
      className={clsx(style.controlsWrapper, {
        [style.disabled]: isNotAuthorized,
      })}
    >
      <div className={style.volumeWrapper}>
        <button onClick={toggleMute}>
          {isMuted ? <IconVolumeMuted /> : isLowVolume ? <IconVolumeMin /> : <IconVolumeMax />}
        </button>
        <input
          type="range"
          className={progressStyle.progressBar}
          min={0}
          max={100}
          value={volume}
          onChange={(e) => setVolume(+e.target.value)}
          style={{
            background: `linear-gradient(to right, var(--color-primary-100) ${volume}%, var(--color-grey-bg) ${volume}%)`,
          }}
        />
      </div>
      <div className={style.controls}>
        <button onClick={skipBackward}>
          <IconBackward />
        </button>
        <button onClick={togglePlayPause}>{isPlaying ? <IconPause /> : <IconPlay />}</button>
        <button onClick={skipForward}>
          <IconForward />
        </button>
      </div>
    </div>
  )
}

export default Controls
